// src/shared/components/TagInput.tsx

import { TagsInput, type ComboboxItem, type OptionsFilter } from '@mantine/core';
import { TAG_CONSTRAINTS, TAG_SPLIT_CHARS } from '@/lib/constants/tag';

/**
 * Prefix-based filter for tag suggestions.
 * Only shows tags that start with the typed text (case-insensitive).
 * More predictable than the default substring matching for tag selection.
 */
const prefixFilter: OptionsFilter = ({ options, search }) => {
  const query = search.toLowerCase().trim();
  if (!query) return options;
  return (options as ComboboxItem[]).filter((option) =>
    option.label.toLowerCase().startsWith(query)
  );
};

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  onBlur?: () => void;
  allTags: string[];
  allowNewTags?: boolean;
  maxTags?: number;
  error?: string;
  disabled?: boolean;
  label?: string;
  description?: string;
  placeholder?: string;
}

export function TagInput({
  value,
  onChange,
  onBlur,
  allTags,
  allowNewTags = true,
  maxTags = TAG_CONSTRAINTS.MAX_TAGS_PER_MEDIA,
  error,
  disabled = false,
  label = 'Tags',
  description,
  placeholder = 'Add a tag...',
}: TagInputProps) {
  function handleChange(newTags: string[]): void {
    const processed = newTags.reduce<string[]>((acc, tag) => {
      const trimmed = tag.trim();
      if (!trimmed) return acc;

      // Find canonical casing from allTags (case-insensitive)
      const canonical = allTags.find(
        (t) => t.toLowerCase() === trimmed.toLowerCase()
      );

      // If allowNewTags is false and tag doesn't exist, reject
      if (!allowNewTags && !canonical) return acc;

      const finalTag = canonical ?? trimmed;

      // Case-insensitive duplicate check against accumulated tags
      const isDuplicate = acc.some(
        (t) => t.toLowerCase() === finalTag.toLowerCase()
      );
      if (isDuplicate) return acc;

      acc.push(finalTag);
      return acc;
    }, []);

    onChange(processed);
  }

  const tagCount = `${value.length}/${maxTags} tags`;

  return (
    <TagsInput
      label={label}
      description={description ?? tagCount}
      placeholder={value.length >= maxTags ? undefined : placeholder}
      value={value}
      onChange={handleChange}
      onBlur={onBlur}
      data={allTags}
      maxTags={maxTags}
      splitChars={[...TAG_SPLIT_CHARS]}
      allowDuplicates={false}
      acceptValueOnBlur={false}
      filter={prefixFilter}
      limit={TAG_CONSTRAINTS.MAX_SUGGESTIONS_SHOWN}
      styles={{
        pill: {
          backgroundColor: 'var(--mantine-primary-color-light)',
          color: 'var(--mantine-primary-color-filled)',
        },
      }}
      error={error}
      disabled={disabled}
    />
  );
}
